import { ChecklistTemplate } from './types';

/**
 * Plantilla maestra de revisión para sistemas de Intrusión (I&HAS).
 * Basada en:
 *   - RD 2364/1994 Reglamento de Seguridad Privada y normativa de desarrollo
 *     (Orden INT/316/2011 sobre funcionamiento de sistemas de alarma).
 *   - UNE EN 50131 (sistemas de alarma de intrusión y atraco) y UNE-CLC/TS 50131-7.
 *   - Ley 5/2014 de Seguridad Privada.
 *
 * Periodicidades aplicadas:
 *   T = Trimestral (revisión preventiva mínima exigida por normativa)
 *   A = Anual (revisión completa).
 */
export const INTRUSION_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'intrusión',
  titulo: 'Sistema de Intrusión y Atraco (I&HAS) — UNE EN 50131',
  protocolos: [
    {
      id: 'central',
      titulo: '1. Central de intrusión',
      cabecera: [
        { key: 'cantidad', label: 'Nº de centrales', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'grado', label: 'Grado de seguridad (UNE EN 50131-1)' },
        { key: 'clase_ambiental', label: 'Clase ambiental' },
        { key: 'zonas_total', label: 'Nº zonas totales', tipo: 'number' },
        { key: 'zonas_ocupadas', label: 'Zonas ocupadas', tipo: 'number' },
        { key: 'particiones', label: 'Nº de particiones / áreas', tipo: 'number' },
        { key: 'tipo_baterias', label: 'Tipo / capacidad baterías' },
        { key: 'fecha_fab_baterias', label: 'Fecha fabricación baterías', tipo: 'date' },
        { key: 'comunicador', label: 'Comunicador (IP / GSM / RTC / radio)' },
        { key: 'firmware', label: 'Versión firmware' },
      ],
      preguntas: [
        { id: 'I.1.1', texto: '¿Dispone la central de marcado CE y de declaración de conformidad con UNE EN 50131-3?', periodicidad: ['T','A'] },
        { id: 'I.1.2', texto: '¿El grado de seguridad de la central es el adecuado al riesgo del establecimiento (UNE EN 50131-1)?', periodicidad: ['A'] },
        { id: 'I.1.3', texto: '¿Está instalada en zona protegida, con tamper de tapa y de arranque operativos?', periodicidad: ['T','A'] },
        { id: 'I.1.4', texto: '¿Funcionan correctamente las indicaciones ópticas y acústicas de estado (armado, desarmado, alarma, avería, sabotaje)?', periodicidad: ['T','A'] },
        { id: 'I.1.5', texto: '¿Se ha verificado el registro de eventos (memoria) y la fecha/hora del sistema?', periodicidad: ['T','A'] },
        { id: 'I.1.6', texto: '¿Existe alimentación primaria independiente y protegida frente a manipulación?', periodicidad: ['A'] },
        { id: 'I.1.7', texto: '¿Se ha comprobado el funcionamiento con cada fuente de suministro (red y batería)?', periodicidad: ['T','A'] },
        { id: 'I.1.8', texto: '¿El estado de carga de las baterías es correcto y dentro del periodo de vida útil del fabricante?', periodicidad: ['T','A'] },
        { id: 'I.1.9', texto: '¿Se ha realizado limpieza de bornas y comprobación de tensiones de carga / reposo / alarma?', periodicidad: ['A'] },
        { id: 'I.1.10', texto: '¿La autonomía en ausencia de red cumple el grado declarado (≥12 h grado 2, ≥60 h grado 3)?', periodicidad: ['A'] },
        { id: 'I.1.11', texto: '¿Funciona correctamente la señalización de avería de red y de batería?', periodicidad: ['T','A'] },
        { id: 'I.1.12', texto: '¿Están protegidos los códigos de usuario/instalador y se ha cambiado el código maestro por defecto?', periodicidad: ['A'] },
        { id: 'I.1.13', texto: '¿La programación de zonas, particiones y horarios coincide con la instalación real?', periodicidad: ['A'] },
        { id: 'I.1.14', texto: '¿Se ejecuta correctamente el autotest periódico del sistema?', periodicidad: ['T','A'] },
        { id: 'I.1.15', texto: '¿Está el firmware actualizado conforme a recomendaciones del fabricante?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'teclados',
      titulo: '2. Teclados y dispositivos de armado',
      cabecera: [
        { key: 'cantidad', label: 'Cantidad', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'tipo', label: 'Tipo (LCD / táctil / lector de proximidad)' },
      ],
      preguntas: [
        { id: 'I.2.1', texto: '¿Disponen los teclados / lectores de marcado CE y son del grado adecuado al sistema?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.2.2', texto: '¿Están ubicados en zona protegida y accesibles únicamente al personal autorizado?', periodicidad: ['T','A'] },
        { id: 'I.2.3', texto: '¿Funcionan correctamente todas las teclas, indicadores y pantalla?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.2.4', texto: '¿Operan correctamente el antisabotaje de tapa y de arranque?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.2.5', texto: '¿Se ha verificado el armado / desarmado por partición desde cada teclado?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.2.6', texto: '¿Se ha probado la función de coacción y la generación de su señal específica a CRA?', periodicidad: ['A'] },
        { id: 'I.2.7', texto: '¿La señalización de zonas abiertas o averías se muestra correctamente?', periodicidad: ['T','A'] },
      ],
    },
    {
      id: 'volumetricos',
      titulo: '3. Detectores volumétricos (PIR / dual / microondas)',
      cabecera: [
        { key: 'tipo_1', label: 'Tipo detector 1' },
        { key: 'cantidad_1', label: 'Cantidad 1', tipo: 'number' },
        { key: 'marca_1', label: 'Marca 1' },
        { key: 'modelo_1', label: 'Modelo 1' },
        { key: 'tipo_2', label: 'Tipo detector 2' },
        { key: 'cantidad_2', label: 'Cantidad 2', tipo: 'number' },
        { key: 'marca_2', label: 'Marca 2' },
        { key: 'modelo_2', label: 'Modelo 2' },
        { key: 'tipo_3', label: 'Tipo detector 3' },
        { key: 'cantidad_3', label: 'Cantidad 3', tipo: 'number' },
        { key: 'marca_3', label: 'Marca 3' },
        { key: 'modelo_3', label: 'Modelo 3' },
      ],
      preguntas: [
        { id: 'I.3.1', texto: '¿Tienen los detectores marcado CE y son del grado adecuado al sistema (UNE EN 50131-2-X)?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.3.2', texto: '¿La ubicación está libre de fuentes de calor, corrientes de aire y obstáculos que afecten a la cobertura?', periodicidad: ['T','A'] },
        { id: 'I.3.3', texto: '¿Se ha realizado prueba de cobertura (walk-test) en todos los detectores con transmisión correcta a la central?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.3.4', texto: '¿Funciona correctamente el antisabotaje (tamper) de cada detector?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.3.5', texto: '¿La función antimasking se activa y reporta correctamente (cuando aplica al grado del sistema)?', periodicidad: ['A'], unidades: true },
        { id: 'I.3.6', texto: '¿Las áreas a proteger conforme a proyecto están cubiertas en su totalidad?', periodicidad: ['A'] },
        { id: 'I.3.7', texto: '¿Existen detectores con antigüedad superior a la prescrita por el fabricante o con sensibilidad degradada?', periodicidad: ['A'], unidades: true },
        { id: 'I.3.8', texto: '¿Se ha limpiado la óptica / lentes y verificado la sujeción de cada detector?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'perimetrales',
      titulo: '4. Detección perimetral y contactos magnéticos',
      cabecera: [
        { key: 'contactos_cantidad', label: 'Nº contactos magnéticos', tipo: 'number' },
        { key: 'contactos_tipo', label: 'Tipo (superficie / empotrar / alta seguridad)' },
        { key: 'perimetral_tipo', label: 'Detección perimetral (cable / barreras / vibración)' },
        { key: 'perimetral_cantidad', label: 'Nº elementos perimetrales', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
      ],
      preguntas: [
        { id: 'I.4.1', texto: '¿Disponen los contactos magnéticos de marcado CE y son del grado adecuado?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.4.2', texto: '¿La fijación, alineación y holgura entre imán y contacto es la correcta?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.4.3', texto: '¿Se ha probado la apertura/cierre de cada acceso con transmisión correcta a la central?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.4.4', texto: '¿Están protegidos los cableados de los contactos frente a manipulación (canalización oculta)?', periodicidad: ['A'] },
        { id: 'I.4.5', texto: '¿Los detectores perimetrales (barreras IR, microondas, cable sensor, vibración) están alineados y calibrados?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.4.6', texto: '¿Se ha realizado prueba de intrusión real sobre cada zona perimetral con activación correcta?', periodicidad: ['A'], unidades: true },
        { id: 'I.4.7', texto: '¿Los tampers y la supervisión de línea funcionan correctamente en todos los elementos perimetrales?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'detectores_especificos',
      titulo: '5. Detectores específicos (rotura cristal, sísmicos, atraco)',
      cabecera: [
        { key: 'rotura_cantidad', label: 'Nº detectores rotura cristal', tipo: 'number' },
        { key: 'sismicos_cantidad', label: 'Nº detectores sísmicos', tipo: 'number' },
        { key: 'atraco_cantidad', label: 'Nº pulsadores atraco / pánico', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
      ],
      preguntas: [
        { id: 'I.5.1', texto: '¿Los detectores de rotura de cristal están correctamente orientados y a la distancia recomendada por el fabricante?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.5.2', texto: '¿Se ha realizado prueba con simulador acústico/mecánico con activación correcta?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.5.3', texto: '¿Los detectores sísmicos están bien fijados al elemento a proteger y calibrados según fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'I.5.4', texto: '¿Se ha realizado autotest de los detectores sísmicos con resultado correcto?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.5.5', texto: '¿Los pulsadores de atraco / coacción están ubicados de forma accesible y discreta para el usuario?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.5.6', texto: '¿Se ha probado cada pulsador de atraco con generación de señal silenciosa específica a CRA?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'sirenas',
      titulo: '6. Sirenas interior y exterior',
      cabecera: [
        { key: 'interior_cantidad', label: 'Nº sirenas interior', tipo: 'number' },
        { key: 'exterior_cantidad', label: 'Nº sirenas exterior', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'autoalimentadas', label: 'Autoalimentadas (sí/no)' },
        { key: 'fecha_fab_baterias', label: 'Fecha fabricación baterías sirena', tipo: 'date' },
      ],
      preguntas: [
        { id: 'I.6.1', texto: '¿Disponen las sirenas de marcado CE y son del grado adecuado (UNE EN 50131-4)?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.6.2', texto: '¿Funcionan correctamente los indicadores acústicos y luminosos al activarse?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.6.3', texto: '¿El antisabotaje de tapa, de arranque y de cableado es operativo en todas las sirenas?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.6.4', texto: '¿El estado y autonomía de la batería propia de la sirena exterior es correcto?', periodicidad: ['T','A'], unidades: true },
        { id: 'I.6.5', texto: '¿La sirena exterior es claramente visible desde la vía pública y está protegida frente a manipulación?', periodicidad: ['A'] },
        { id: 'I.6.6', texto: '¿Existe al menos una sirena exterior y una interior, conforme a la normativa aplicable?', periodicidad: ['A'] },
        { id: 'I.6.7', texto: '¿El nivel sonoro cumple los valores establecidos por la normativa local y por el fabricante?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'transmision',
      titulo: '7. Transmisión a CRA y comunicaciones',
      cabecera: [
        { key: 'cra_nombre', label: 'CRA receptora' },
        { key: 'numero_abonado', label: 'Nº de abonado' },
        { key: 'via_principal', label: 'Vía principal (IP / GSM / GPRS / RTC)' },
        { key: 'via_respaldo', label: 'Vía de respaldo' },
        { key: 'periodicidad_test', label: 'Periodicidad test automático' },
        { key: 'grado_atp', label: 'Grado ATP / SP (UNE EN 50136)' },
      ],
      preguntas: [
        { id: 'I.7.1', texto: '¿Existe vía de transmisión principal y vía de respaldo conforme al grado del sistema (Orden INT/316/2011)?', periodicidad: ['T','A'] },
        { id: 'I.7.2', texto: '¿La supervisión de las vías de comunicación cumple los tiempos exigidos por el grado (UNE EN 50136)?', periodicidad: ['T','A'] },
        { id: 'I.7.3', texto: '¿Se reciben correctamente en CRA las señales de alarma, sabotaje, avería, coacción y atraco diferenciadas?', periodicidad: ['T','A'] },
        { id: 'I.7.4', texto: '¿Se realiza y recepciona el test automático periódico de comunicación?', periodicidad: ['T','A'] },
        { id: 'I.7.5', texto: '¿La palabra clave y la palabra de coacción están actualizadas y conocidas por los usuarios autorizados?', periodicidad: ['T','A'] },
        { id: 'I.7.6', texto: '¿Está implementado el protocolo de verificación (secuencial, audio o vídeo) y funciona correctamente?', periodicidad: ['A'] },
        { id: 'I.7.7', texto: '¿La empresa instaladora / mantenedora está inscrita en el Registro del Ministerio del Interior?', periodicidad: ['A'] },
        { id: 'I.7.8', texto: '¿Está disponible y actualizada la documentación del sistema (contrato, planos, manual y libro de mantenimiento)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'cableado',
      titulo: '8. Cableado, alimentación y baterías auxiliares',
      cabecera: [
        { key: 'fuentes_aux', label: 'Nº fuentes alimentación auxiliares', tipo: 'number' },
        { key: 'capacidad', label: 'Capacidad baterías auxiliares' },
        { key: 'fecha_fab', label: 'Fecha fabricación baterías', tipo: 'date' },
      ],
      preguntas: [
        { id: 'I.8.1', texto: '¿El cableado cumple las especificaciones del fabricante y el grado del sistema?', periodicidad: ['A'] },
        { id: 'I.8.2', texto: '¿La canalización es exclusiva del sistema de intrusión o claramente diferenciable de otras instalaciones?', periodicidad: ['A'] },
        { id: 'I.8.3', texto: '¿Las conexiones en cajas de registro y equipos ofrecen la seguridad adecuada y están supervisadas?', periodicidad: ['A'] },
        { id: 'I.8.4', texto: '¿Se ha fijado correctamente la canalización y discurre por zonas protegidas / de bajo riesgo?', periodicidad: ['A'] },
        { id: 'I.8.5', texto: '¿Las fuentes de alimentación auxiliares están supervisadas indicando fallo de red y de batería?', periodicidad: ['T','A'] },
        { id: 'I.8.6', texto: '¿El estado de carga de las baterías auxiliares es correcto?', periodicidad: ['T','A'] },
        { id: 'I.8.7', texto: '¿Las baterías auxiliares están dentro del periodo de vida útil del fabricante (≤ 4 años)?', periodicidad: ['A'] },
        { id: 'I.8.8', texto: '¿Existen planos actualizados con la ubicación de todos los dispositivos y recorridos de cableado?', periodicidad: ['A'] },
      ],
    },
  ],
};
